/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo, useRef, useState } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Button, Card, Form as NXUIForm } from '@nexigendigital/nxui'
import { domainAPI, useUpdateNameserversMutation } from 'api/domain'
import { DNSModeType } from 'models/enums'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function EditNameserversForm() {
    const editNameserversFormRef = useRef<FormikProps<{ nameservers: string[] }>>(null)
    const id = +location.pathname?.split('/')[2]
    const { data: { nameservers } = { nameservers: [] }, isError } = domainAPI.endpoints.dnsRecords.useQueryState(id)
    const [updateNameservers, { isLoading }] = useUpdateNameserversMutation()
    const [mode, setMode] = useState<'view' | 'edit'>('view')
    const column = useMemo(() => {
        if (isError) {
            return []
        }

        return [
            {
                Header: 'Custom Nameservers',
                accessor: 'nameservers'
            },
            {
                Header: '',
                accessor: 'action',
                align: 'right'
            }
        ]
    }, [])
    const data = useMemo(() => {
        if (isError) {
            return []
        }

        return nameservers.map((_, index) => ({
            nameservers: (
                <Field
                    name={`nameservers.${index}`}
                    type={'text'}
                    component={NXUIForm.TableField}
                    table={true}
                />
            ),
            action: (
                <Anchor
                    color={isLoading ? 'secondary' : 'error'}
                    disabled={isLoading}
                    onClick={() => {
                        if (editNameserversFormRef.current) {
                            updateNameservers({
                                id,
                                nameservers: editNameserversFormRef.current.values.nameservers.filter((_: string, i: number) => i !== index),
                                dns_config: DNSModeType.CustomNameservers
                            })
                        }
                    }}
                >
                    Delete
                </Anchor>
            )
        }))
    }, [nameservers, isLoading])

    function footer() {
        if (mode === 'view') {
            return (
                <FooterWrapper>
                    <Actions>
                        <Button
                            type={'button'}
                            color={'primary'}
                            onClick={(e: MouseEvent) => {
                                e.preventDefault()
                                setMode('edit')
                            }}
                        >
                            Edit
                        </Button>
                    </Actions>
                </FooterWrapper>
            )
        }

        return (
            <FooterWrapper>
                <Actions>
                    <Button
                        type={'button'}
                        color={'secondary'}
                        onClick={() => setMode('view')}
                    >
                        Back
                    </Button>
                    <Button
                        type={'submit'}
                        color={'primary'}
                        disabled={isLoading}
                        form={'nameserversForm'}
                    >
                        Save
                    </Button>
                </Actions>
            </FooterWrapper>
        )
    }

    if (mode === 'view') {
        return (
            <Card
                table={{
                    render: {
                        columns: () => column,
                        data: () => nameservers.map((nameserver) => ({ nameservers: nameserver }))
                    }
                }}
                footer={footer()}
            />
        )
    }

    return (
        <Formik
            initialValues={{
                nameservers: nameservers
            }}
            innerRef={editNameserversFormRef}
            enableReinitialize={true}
            onSubmit={({ nameservers }, { setSubmitting, resetForm }) => {
                updateNameservers({
                    id,
                    nameservers,
                    dns_config: DNSModeType.CustomNameservers
                })
                setSubmitting(false)
                resetForm()
            }}
        >
            {() => (
                <Form id={'nameserversForm'}>
                    <Card
                        table={{
                            render: {
                                columns: () => column,
                                data: () => data
                            }
                        }}
                        footer={footer()}
                    />
                </Form>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const FooterWrapper = styled.div`
    border-top: 1px solid ${props => props.theme['gray--100']};
`

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 12px 12px 12px 0;
`
